import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

const SkillCluster = ({ results, typeMapping }) => {
  const clusterData = useMemo(() => {
    if (!results || !typeMapping) return [];
    
    const timeSpent = results.questions.find(q => q.questionKey === 'time_spent')?.rankings || [];
    const timeDesired = results.questions.find(q => q.questionKey === 'time_desired')?.rankings || [];
    const competencies = results.questions.find(q => q.questionKey === 'competency')?.rankings || [];

    // Initialize data structure for each type
    const clusters = Object.keys(typeMapping).map(type => ({
      type,
      timeSpent: 0,
      timeDesired: 0,
      competency: 0,
      activities: []
    }));

    // Process each activity
    timeSpent.forEach(activity => {
      const matchingDesired = timeDesired.find(d => d.item === activity.item);
      const matchingCompetency = competencies.find(c => c.item === activity.item);

      // Find matching type(s)
      Object.entries(typeMapping).forEach(([type, items]) => {
        if (items.includes(activity.item)) {
          const clusterIndex = clusters.findIndex(c => c.type === type);
          if (clusterIndex !== -1) {
            clusters[clusterIndex].activities.push({
              name: activity.item,
              timeSpent: parseFloat(activity.score),
              timeDesired: matchingDesired ? parseFloat(matchingDesired.score) : 0,
              competency: matchingCompetency ? parseFloat(matchingCompetency.score) : 0
            });
          }
        }
      });
    });

    // Calculate raw scores
    const rawScores = clusters.map(cluster => ({
      ...cluster,
      timeSpent: cluster.activities.length > 0 
        ? cluster.activities.reduce((sum, act) => sum + act.timeSpent, 0) / cluster.activities.length 
        : 0,
      timeDesired: cluster.activities.length > 0
        ? cluster.activities.reduce((sum, act) => sum + act.timeDesired, 0) / cluster.activities.length
        : 0,
      competency: cluster.activities.length > 0
        ? cluster.activities.reduce((sum, act) => sum + act.competency, 0) / cluster.activities.length
        : 0
    }));

    // Calculate total scores for each metric
    const totals = rawScores.reduce((acc, cluster) => ({
      timeSpent: acc.timeSpent + cluster.timeSpent,
      timeDesired: acc.timeDesired + cluster.timeDesired,
      competency: acc.competency + cluster.competency
    }), { timeSpent: 0, timeDesired: 0, competency: 0 });

    // Normalize scores to ensure sum equals 1 for each metric
    const normalizedScores = rawScores.map(cluster => ({
      ...cluster,
      timeSpent: totals.timeSpent > 0 ? Number((cluster.timeSpent / totals.timeSpent).toFixed(2)) : 0,
      timeDesired: totals.timeDesired > 0 ? Number((cluster.timeDesired / totals.timeDesired).toFixed(2)) : 0,
      competency: totals.competency > 0 ? Number((cluster.competency / totals.competency).toFixed(2)) : 0
    }));

    // After normalizing scores, find the maximum value
    const maxValue = normalizedScores.reduce((max, cluster) => {
      return Math.max(
        max,
        cluster.timeSpent,
        cluster.timeDesired,
        cluster.competency
      );
    }, 0);

    return {
      data: normalizedScores,
      maxValue
    };
  }, [results, typeMapping]);

  if (!results || !typeMapping || clusterData.data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Analyse par Clusters        </Typography>
        <Typography color="text.secondary">
          Données insuffisantes pour générer l'analyse
        </Typography>
      </Paper>
    );
  }

  const renderRadarChart = (dataKey, title, color) => (
    <Grid item xs={12} md={4} sx={{ height: 300 }}>
      <Typography variant="subtitle1" align="center" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ width: '100%', height: 'calc(100% - 32px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={clusterData.data}>
            <PolarGrid />
            <PolarAngleAxis dataKey="type" />
            <PolarRadiusAxis 
              domain={[0, Math.ceil(clusterData.maxValue * 10) / 10]} 
              tickCount={5}
            />
            <Radar
              name={title}
              dataKey={dataKey}
              stroke={color}
              fill={color}
              fillOpacity={0.6}
            />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      </Box>
    </Grid>
  );

  return (
    <Paper sx={{ p: 3, height: '100%', minHeight: 500 }}>
      <Typography variant="h6" gutterBottom>
        Analyse par Clusters
      </Typography>
      
      <Box sx={{ 
        width: '100%', 
        height: 'calc(100% - 32px)', // Subtract typography height
        display: 'flex',
        flexDirection: 'column' 
      }}>
        <Grid container spacing={3} sx={{ flexGrow: 1 }}>
          {renderRadarChart('timeSpent', 'Temps Actuel', '#82ca9d')}
          {renderRadarChart('timeDesired', 'Temps Souhaité', '#ffc658')}
          {renderRadarChart('competency', 'Compétence', '#8884d8')}
        </Grid>
      </Box>
    </Paper>
  );
};

export default SkillCluster;